import React, { useState } from 'react';
import { useCars } from '../context/CarsContext';
import { Car } from '../components/CarCard';
import ImageUpload from '../components/ui/ImageUpload';
import './AdminDashboard.css';

type ModalMode = 'closed' | 'add' | 'edit';

const emptyForm = {
  title: '',
  price: '',
  features: '',
  issues: '',
  images: [] as string[],
  photoLink: '',
  status: 'available' as 'available' | 'sold',
};

function extractYear(title: string): number {
  const match = title.match(/\b(19|20)\d{2}\b/);
  return match ? parseInt(match[0]) : new Date().getFullYear();
}

const AdminDashboard: React.FC = () => {
  const { cars, addCar, updateCar, deleteCar, reorderCars } = useCars();

  const [modalMode, setModalMode] = useState<ModalMode>('closed');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'available' | 'sold'>('all');

  const openAdd = () => {
    setForm(emptyForm);
    setEditingId(null);
    setModalMode('add');
  };

  const openEdit = (car: Car) => {
    setForm({
      title: car.title,
      price: car.price,
      features: car.features.join('\n'),
      issues: (car.issues || []).join('\n'),
      images: car.images,
      photoLink: car.photoLink || '',
      status: car.status,
    });
    setEditingId(car.id);
    setModalMode('edit');
  };

  const closeModal = () => {
    setModalMode('closed');
    setEditingId(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    const carData = {
      title: form.title,
      year: extractYear(form.title),
      make: '',
      model: '',
      mileage: '',
      price: form.price.trim() || 'Contact Us',
      description: '',
      features: form.features.split('\n').filter(f => f.trim()),
      issues: form.issues.split('\n').filter(i => i.trim()),
      images: form.images,
      photoLink: form.photoLink.trim() || '',
      status: form.status as 'available' | 'sold',
      datePosted: new Date().toISOString().split('T')[0],
    };

    try {
      if (modalMode === 'add') {
        await addCar(carData);
      } else if (modalMode === 'edit' && editingId) {
        await updateCar(editingId, carData);
      }
      closeModal();
    } catch (err) {
      console.error('Save failed:', err);
      alert('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = (id: string) => {
    if (deleteConfirm === id) {
      deleteCar(id);
      setDeleteConfirm(null);
    } else {
      setDeleteConfirm(id);
      setTimeout(() => setDeleteConfirm(null), 3000);
    }
  };

  const toggleStatus = (car: Car) => {
    updateCar(car.id, { status: car.status === 'available' ? 'sold' : 'available' });
  };

  const moveUp = (carId: string) => {
    const idx = cars.findIndex(c => c.id === carId);
    if (idx > 0) reorderCars(idx, idx - 1);
  };

  const moveDown = (carId: string) => {
    const idx = cars.findIndex(c => c.id === carId);
    if (idx < cars.length - 1) reorderCars(idx, idx + 1);
  };

  const filteredCars = cars.filter(car => {
    const matchesSearch = car.title.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filterStatus === 'all' || car.status === filterStatus;
    return matchesSearch && matchesFilter;
  });

  const availableCount = cars.filter(c => c.status === 'available').length;
  const soldCount = cars.filter(c => c.status === 'sold').length;

  return (
    <div className="admin">
      <main className="admin-main">
        {/* Stats */}
        <div className="admin-stats">
          <div className="admin-stat">
            <span className="admin-stat-num">{cars.length}</span>
            <span className="admin-stat-label">Total</span>
          </div>
          <div className="admin-stat">
            <span className="admin-stat-num stat-green">{availableCount}</span>
            <span className="admin-stat-label">Available</span>
          </div>
          <div className="admin-stat">
            <span className="admin-stat-num stat-red">{soldCount}</span>
            <span className="admin-stat-label">Sold</span>
          </div>
        </div>

        {/* Toolbar */}
        <div className="admin-toolbar">
          <div className="toolbar-left">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="search-input"
            />
            <div className="toolbar-filters">
              <button className={`toolbar-filter ${filterStatus === 'all' ? 'active' : ''}`} onClick={() => setFilterStatus('all')}>All</button>
              <button className={`toolbar-filter ${filterStatus === 'available' ? 'active' : ''}`} onClick={() => setFilterStatus('available')}>Available</button>
              <button className={`toolbar-filter ${filterStatus === 'sold' ? 'active' : ''}`} onClick={() => setFilterStatus('sold')}>Sold</button>
            </div>
          </div>
          <button onClick={openAdd} className="add-btn">+ Add Car</button>
        </div>

        {/* Cars List */}
        <div className="admin-cars">
          {filteredCars.length === 0 ? (
            <div className="empty-state">No cars found.</div>
          ) : (
            filteredCars.map(car => (
              <div key={car.id} className="admin-car-row">
                <div className="car-row-order">
                  <button
                    className="order-btn"
                    onClick={() => moveUp(car.id)}
                    disabled={cars.findIndex(c => c.id === car.id) === 0}
                    title="Move up"
                  >▲</button>
                  <span className="order-pos">{cars.findIndex(c => c.id === car.id) + 1}</span>
                  <button
                    className="order-btn"
                    onClick={() => moveDown(car.id)}
                    disabled={cars.findIndex(c => c.id === car.id) === cars.length - 1}
                    title="Move down"
                  >▼</button>
                </div>
                <div className="car-row-info">
                  <h3>{car.title}</h3>
                  {car.features.length > 0 && (
                    <div className="car-row-tags">
                      {car.features.slice(0, 3).map((f, i) => (
                        <span key={i} className="car-row-tag">{f}</span>
                      ))}
                      {car.features.length > 3 && (
                        <span className="car-row-tag tag-muted">+{car.features.length - 3} more</span>
                      )}
                    </div>
                  )}
                </div>
                <div className="car-row-actions">
                  <button
                    className={`status-toggle ${car.status === 'available' ? 'status-available' : 'status-sold'}`}
                    onClick={() => toggleStatus(car)}
                  >
                    {car.status === 'available' ? 'Available' : 'Sold'}
                  </button>
                  <button className="action-btn edit-btn" onClick={() => openEdit(car)}>Edit</button>
                  <button
                    className={`action-btn delete-btn ${deleteConfirm === car.id ? 'delete-confirm' : ''}`}
                    onClick={() => handleDelete(car.id)}
                  >
                    {deleteConfirm === car.id ? 'Confirm?' : 'Delete'}
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </main>

      {/* Modal */}
      {modalMode !== 'closed' && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <span>{modalMode === 'add' ? 'Add Car' : 'Edit Car'}</span>
              <button className="modal-close" onClick={closeModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">

                <div className="form-field">
                  <label>Title</label>
                  <input
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    required
                    placeholder="e.g. 1998 BMW M3 EuroSpec"
                  />
                </div>

                <div className="form-field">
                  <label>Price <span className="field-hint">— leave blank to show "Contact Us"</span></label>
                  <input
                    name="price"
                    value={form.price}
                    onChange={handleChange}
                    placeholder="e.g. $24,500 or Available May 1st, 2026"
                  />
                </div>

                <div className="form-field">
                  <label>Features <span className="field-hint">— one per line</span></label>
                  <textarea
                    name="features"
                    value={form.features}
                    onChange={handleChange}
                    rows={5}
                    placeholder={"3.2L S50B32 Engine\n6-Speed Manual\nEstoril Blue"}
                  />
                </div>

                <div className="form-field">
                  <label>Issues / Notes <span className="field-hint">— one per line</span></label>
                  <textarea
                    name="issues"
                    value={form.issues}
                    onChange={handleChange}
                    rows={3}
                    placeholder={"Needs new tires\nABS light on"}
                  />
                </div>

                <div className="form-field">
                  <label>Images <span className="field-hint">— first image is the cover</span></label>
                  <ImageUpload
                    value={form.images}
                    onChange={(imgs) => setForm(prev => ({ ...prev, images: imgs }))}
                  />
                </div>

                <div className="form-field">
                  <label>Status</label>
                  <select name="status" value={form.status} onChange={handleChange}>
                    <option value="available">Available</option>
                    <option value="sold">Sold</option>
                  </select>
                </div>

                <div className="form-field form-field-optional">
                  <label>Photo Album Link <span className="field-hint">— optional, shows "View Photos" button on listing</span></label>
                  <input
                    name="photoLink"
                    value={form.photoLink}
                    onChange={handleChange}
                    placeholder="https://photos.app.goo.gl/..."
                  />
                </div>

              </div>
              <div className="modal-footer">
                <button type="button" className="modal-cancel" onClick={closeModal}>Cancel</button>
                <button type="submit" className="modal-submit" disabled={saving}>
                  {saving ? 'Saving…' : modalMode === 'add' ? 'Add Car' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
